<script setup lang="ts">
/**
 * The sign-in layout: a navy brand band with the beacon, and one card.
 *
 * From the 2026-09-26 sign-in design ("1a", and "1a on tablet and desktop"). Below
 * 1024px the band runs across the top and the card lifts over it; from 1024px the
 * band becomes a side panel and the card sits in the page to its right. The
 * screen inside decides nothing about that — it lays out its heading and its card
 * against `--sign-in-band`, which this shell owns.
 *
 * **The same four landmarks as every other layout** (`FND-11`, asserted in
 * `shell.spec.ts`). The design shows no header bar and no footer, so the banner is
 * the brand on the band and the footer is one quiet line at the foot of the page.
 * The `navigation` landmark belongs to the screen: "New provider? Create a free
 * profile", directly under the card, which is where the design puts it and the
 * one way off this screen it offers.
 *
 * The theme switch moved to the footer. The design does not show one, and the
 * signed-out header that used to carry it is gone here; dropping it would strand
 * someone who chose dark before signing out.
 */

import { computed, ref } from 'vue'
import { useRoute } from 'vue-router'

import ThemeMenu from './ThemeMenu.vue'

import brandMark from '@/assets/MatchPointIcon.svg'
import MpBeacon from '@/components/base/MpBeacon.vue'
import MpToaster from '@/components/base/MpToaster.vue'
import ErrorBoundary from '@/components/state/ErrorBoundary.vue'
import { useMainFocus } from '@/lib/route-focus'
import { SCREENS_BY_ID } from '@/screens/catalog'

const route = useRoute()
const screen = computed(() => SCREENS_BY_ID[route.meta.screenId])

const main = ref<HTMLElement | null>(null)
useMainFocus(main)
</script>

<template>
  <div class="mp-sign-in">
    <a
      class="mp-sr-only"
      href="#mp-main"
    >Skip to main content</a>

    <!-- Decorative ground: the band and the beacon on it. -->
    <div
      class="mp-sign-in__band"
      aria-hidden="true"
    >
      <MpBeacon
        class="mp-sign-in__beacon"
        size="lg"
      />
    </div>

    <header
      class="mp-sign-in__header"
      role="banner"
    >
      <RouterLink
        class="mp-sign-in__brand"
        to="/"
      >
        <span class="mp-sign-in__mark">
          <img
            :src="brandMark"
            alt=""
            aria-hidden="true"
          >
        </span>
        Match&nbsp;Point
      </RouterLink>
    </header>

    <main
      id="mp-main"
      ref="main"
      class="mp-sign-in__main"
      role="main"
      tabindex="-1"
      :aria-label="screen.title"
    >
      <ErrorBoundary>
        <RouterView />
      </ErrorBoundary>
    </main>

    <footer
      class="mp-sign-in__footer"
      role="contentinfo"
    >
      <div class="mp-sign-in__meta">
        <RouterLink to="/legal">
          Terms and privacy
        </RouterLink>
        <ThemeMenu />
      </div>
    </footer>

    <MpToaster />
  </div>
</template>

<style scoped>
.mp-sign-in {
  /* The band's extent. Below 1024px, its height across the top; from 1024px, its
     width down the side. The screen reads this to place its heading on the band. */
  --sign-in-band: calc(var(--space-16) * 5.5);

  position: relative;
  display: flex;
  flex-direction: column;
  min-block-size: 100vh;
  min-block-size: 100dvh;
  background: var(--surface-page);
  overflow-x: clip;
}

.mp-sign-in__band {
  position: absolute;
  inset-block-start: 0;
  inset-inline: 0;
  block-size: var(--sign-in-band);
  background: var(--surface-nav);
  overflow: hidden;
}

/* Up and to the right of the heading, as in the phone and tablet designs. */
.mp-sign-in__beacon {
  position: absolute;
  inset-block-start: 30%;
  inset-inline-end: 18%;
}

.mp-sign-in__header {
  position: relative;
  padding: var(--space-8) var(--space-6) 0;
}

.mp-sign-in__brand {
  display: inline-flex;
  align-items: center;
  gap: var(--space-3);
  font-family: var(--font-heading);
  font-size: var(--font-size-h4);
  font-weight: var(--heading-weight);
  letter-spacing: var(--heading-tracking);
  color: var(--text-on-nav);
  text-decoration: none;
}

.mp-sign-in__brand:visited {
  color: var(--text-on-nav);
}

.mp-sign-in__brand:focus-visible {
  outline: none;
  border-radius: var(--radius-control);
  box-shadow: var(--focus-ring);
}

/* The mark sits on a raised tile so it reads on the navy in both themes. */
.mp-sign-in__mark {
  display: grid;
  place-items: center;
  inline-size: var(--space-10);
  block-size: var(--space-10);
  border-radius: var(--radius-card);
  background: var(--surface-raised);
}

.mp-sign-in__mark img {
  inline-size: 70%;
  block-size: 70%;
}

.mp-sign-in__main {
  position: relative;
  flex: 1;
  padding: var(--space-6) var(--space-4) 0;
}

.mp-sign-in__main:focus-visible {
  outline: none;
}

.mp-sign-in__footer {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--space-5);
  padding: var(--space-6) var(--space-4) var(--space-8);
  font-size: var(--font-size-small);
  color: var(--text-secondary);
}

.mp-sign-in__meta {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  gap: var(--space-5);
  color: var(--text-muted);
}

/* Tablet: the heading centers over the card, as in the iPad design. */
@media (min-width: 768px) {
  .mp-sign-in__header {
    padding-inline: var(--space-12);
  }
}

/* Desktop: the band becomes a side panel; the card and footer take the rest. */
@media (min-width: 1024px) {
  .mp-sign-in {
    --sign-in-band: min(42vw, calc(var(--space-16) * 9.5));
  }

  .mp-sign-in__band {
    inset-block: 0;
    inset-inline-end: auto;
    inline-size: var(--sign-in-band);
    block-size: auto;
  }

  /* Low and to the right, as in the desktop design. */
  .mp-sign-in__beacon {
    inset-block-start: auto;
    inset-block-end: 18%;
    inset-inline-end: 14%;
  }

  .mp-sign-in__header {
    padding: var(--space-12) var(--space-12) 0;
  }

  .mp-sign-in__main {
    display: flex;
    padding: 0;
  }

  .mp-sign-in__footer {
    margin-inline-start: var(--sign-in-band);
  }
}
</style>
